import React from 'react'
import {Box} from '@mui/material'
import Card from 'src/components-new/layout/Card'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import SelectNew from 'src/components-new/inputs/SelectNew'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import CheckboxGroupNew from 'src/components-new/inputs/CheckboxGroupNew'
import PasswordFieldNew from 'src/components-new/inputs/PasswordFieldNew'
import {AddressValue, CaregiverValue, MainDataValue} from '../patientDigitalEnrollmentValidation'
import AddressFields from './AddressFields'

const GENDERS = [
  {value: 'FEMALE', label: 'Femmina'},
  {value: 'MALE', label: 'Maschio'},
  {value: 'NOT_DECLARED', label: 'Non dichiarato'},
]

const PREFERRED_CONTACT_METHODS_OPTIONS = [
  {value: 'PHONE_NUMBER', label: 'Telefono'},
  {value: 'CELL_PHONE_NUMBER', label: 'Cellulare'},
  {value: 'EMAIL', label: 'Email'},
  {value: 'SMS', label: 'SMS'},
]

interface DataStepProps {
  mainData: MainDataValue
  onMainDataChange: (value: MainDataValue) => void
  address: AddressValue
  onAddressChange: (value: AddressValue) => void
  caregiver: CaregiverValue
  onCaregiverChange: (value: CaregiverValue) => void
  errors: Record<string, string>
  disabled?: boolean
}

const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
  gap: 2,
} as const

/**
 * Step 1/2 — new-style equivalent of the three "Dati principali" / "Indirizzo" / "Dati
 * caregiver" cards shown together in step 0 of the legacy
 * PatientDigitalEnrollment.tsx:180-477 (same grouping, only the outer AuthLayout wrapper
 * and Card/field components are new). birthDate, cellPhoneNumber, preferredContactMethods
 * and all caregiver fields stay optional — no rules exist for them in the legacy Form.Item
 * definitions either.
 */
export default function DataStep({
  mainData,
  onMainDataChange,
  address,
  onAddressChange,
  caregiver,
  onCaregiverChange,
  errors,
  disabled,
}: DataStepProps): JSX.Element {
  const setMainField = <K extends keyof MainDataValue>(key: K, next: MainDataValue[K]) => {
    onMainDataChange({...mainData, [key]: next})
  }

  const setCaregiverField = <K extends keyof CaregiverValue>(key: K, next: CaregiverValue[K]) => {
    onCaregiverChange({...caregiver, [key]: next})
  }

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      <Card title="Dati principali">
        <Box sx={GRID_SX}>
          <TextFieldNew label="Nome" value={mainData.firstName} onChange={() => undefined} disabled fullWidth />
          <TextFieldNew label="Cognome" value={mainData.lastName} onChange={() => undefined} disabled fullWidth />

          <SelectNew
            label="Sesso"
            value={mainData.sex}
            onChange={(sex) => setMainField('sex', sex)}
            options={GENDERS}
            required
            fullWidth
            disabled={disabled}
            error={Boolean(errors.sex)}
            helperText={errors.sex}
          />

          <DatePickerNew
            label="Data di nascita"
            value={mainData.birthDate}
            onChange={(birthDate) => setMainField('birthDate', birthDate)}
            fullWidth
            disabled={disabled}
          />

          <TextFieldNew
            label="Email"
            type="email"
            value={mainData.email}
            onChange={(email) => setMainField('email', email)}
            required
            fullWidth
            disabled={disabled}
            error={Boolean(errors.email)}
            helperText={errors.email}
          />

          <TextFieldNew
            label="Numero cellulare"
            type="tel"
            value={mainData.cellPhoneNumber}
            onChange={(cellPhoneNumber) => setMainField('cellPhoneNumber', cellPhoneNumber)}
            fullWidth
            disabled={disabled}
          />

          <CheckboxGroupNew
            label="Metodo di contatto"
            value={mainData.preferredContactMethods}
            onChange={(preferredContactMethods) => setMainField('preferredContactMethods', preferredContactMethods)}
            options={PREFERRED_CONTACT_METHODS_OPTIONS}
            disabled={disabled}
            sx={{gridColumn: {sm: '1 / 3'}}}
          />

          <TextFieldNew
            label="Username"
            value={mainData.username}
            onChange={(username) => setMainField('username', username)}
            required
            fullWidth
            disabled={disabled}
            error={Boolean(errors.username)}
            helperText={errors.username}
          />

          <PasswordFieldNew
            label="Password"
            value={mainData.password}
            onChange={(password) => setMainField('password', password)}
            autoComplete="new-password"
            required
            fullWidth
            disabled={disabled}
            error={Boolean(errors.password)}
            helperText={errors.password}
          />

          <PasswordFieldNew
            label="Conferma password"
            value={mainData.confirmPassword}
            onChange={(confirmPassword) => setMainField('confirmPassword', confirmPassword)}
            autoComplete="new-password"
            required
            fullWidth
            disabled={disabled}
            error={Boolean(errors.confirmPassword)}
            helperText={errors.confirmPassword}
          />
        </Box>
      </Card>

      <Card title="Indirizzo">
        <AddressFields value={address} onChange={onAddressChange} errors={errors} disabled={disabled} />
      </Card>

      <Card title="Dati caregiver">
        <Box sx={GRID_SX}>
          <TextFieldNew
            label="Nome"
            value={caregiver.caregiverFirstName}
            onChange={(v) => setCaregiverField('caregiverFirstName', v)}
            fullWidth
            disabled={disabled}
          />
          <TextFieldNew
            label="Cognome"
            value={caregiver.caregiverLastName}
            onChange={(v) => setCaregiverField('caregiverLastName', v)}
            fullWidth
            disabled={disabled}
          />
          <TextFieldNew
            label="Numero di telefono"
            type="tel"
            value={caregiver.caregiverPhoneNumber}
            onChange={(v) => setCaregiverField('caregiverPhoneNumber', v)}
            fullWidth
            disabled={disabled}
          />
          <TextFieldNew
            label="Numero di cellulare"
            type="tel"
            value={caregiver.caregiverCellPhoneNumber}
            onChange={(v) => setCaregiverField('caregiverCellPhoneNumber', v)}
            fullWidth
            disabled={disabled}
          />
          <TextFieldNew
            label="Email"
            type="email"
            value={caregiver.caregiverEmail}
            onChange={(v) => setCaregiverField('caregiverEmail', v)}
            fullWidth
            disabled={disabled}
            error={Boolean(errors.caregiverEmail)}
            helperText={errors.caregiverEmail}
          />
        </Box>
      </Card>
    </Box>
  )
}
